<template>
  <div>
    <v-card class="my-6 pa-6">
      <div class="d-flex justify-end mb-4">
        <v-switch
          v-model="showIssues"
          label="Afficher les tickets"
          hide-details
          class="mt-0"
        ></v-switch>
      </div>
      <v-row>
        <v-col cols="2">
          <div class="tabs-container">
            <div
              v-for="(tab, index) in tabs"
              :key="index"
              :class="['tab-selector', { selected: selectedTab === index }]"
              @click="selectTab(index)"
            >
              {{ tab.toUpperCase() }}
              <br />
              <template v-if="globalData">
                <StatusCardWithoutHover :type="tab" :values="globalData[tab]" />
              </template>
            </div>
          </div>
        </v-col>
        <v-col cols="10">
          <div class="graph-container">
            <metric-graph
              :metric="selectedMetric"
              :series="series"
              :labels="labels"
              :colors="colors"
              :annotations="annotations"
            />
          </div>
        </v-col>
      </v-row>
    </v-card>
    <MetricsByContentTypeTable :items="contentTypes" />
  </div>
</template>

<script>
import { mdiCircle, mdiCircleOutline } from "@mdi/js";
import useAxios from "@/hooks/useAxios";
import MetricGraph from "@/components/web-perf/cwv/cwv-performance-breakdown/charts/MetricGraph.vue";
import {
  getDeviceName,
  fullRanges,
  rangesColors,
  formatCls,
} from "@/components/web-perf/cwv/utils/utils";
import StatusCardWithoutHover from "@/components/web-perf/cwv/cwv-performance-breakdown/cards/StatusCardWithoutHover.vue";
import MetricsByContentTypeTable from "@/components/web-perf/cwv/cwv-performance-breakdown/tables/MetricsByContentTypeTable.vue";

export default {
  name: "MetricsBreakdownCard",
  components: {
    MetricsByContentTypeTable,
    StatusCardWithoutHover,
    MetricGraph,
  },
  setup() {
    const { axiosGet } = useAxios();

    return {
      axiosGet,
    };
  },
  created() {
    setTimeout(() => {
      this.getData();
      this.getIssues();
    }, 500);
  },
  mounted() {
    // Add click event listener for annotation labels after a delay to ensure chart is rendered
    setTimeout(() => {
      this.attachAnnotationClickListeners();
    }, 1000);
  },
  data() {
    return {
      contentTypes: [],
      item: {
        15: 0,
        16: 0,
        17: 0,
        global: 0,
      },
      globalData: {
        cls: {
          15: 0,
          16: 0,
        },
        lcp: {
          15: 0,
          16: 0,
        },
        inp: {
          15: 0,
          16: 0,
        },
        fcp: {
          15: 0,
          16: 0,
        },
        ttfb: {
          15: 0,
          16: 0,
        },
      },
      annotations: {},
      tabs: ["lcp", "cls", "inp", "fcp", "ttfb"],
      selectedTab: 0, // Default selected tab
      series: [],
      labels: [],
      colors: ["#FF6384", "#36A2EB", "#FFCE56", "#4BC0C0", "#9966FF"],
      icons: {
        mdiCircle,
        mdiCircleOutline,
      },
      issues: [],
      showIssues: true,
      issueAnnotationMap: {},
    };
  },
  methods: {
    getDeviceName,
    async getIssues() {
      await this.$store.dispatch("webPerf/getIssues");
      this.issues = this.$store.getters["webPerf/getIssues"];
    },
    filterIssuesByMetric(metric) {
      if (!this.issues || this.issues.length === 0) return [];
      
      return this.issues.filter(issue => {
        // Include issues that have no metric specified
        if (!issue.metrics || issue.metrics === '') return true;
        
        // Include issues that have the current metric
        const metrics = issue.metrics.toLowerCase().split(',').map(m => m.trim());
        return metrics.includes(metric);
      });
    },
    getXaxisAnnotationsFromIssues(metric) {
      const filteredIssues = this.filterIssuesByMetric(metric);
      
      // Clear previous mapping
      this.issueAnnotationMap = {};
      
      // Get the date range from labels (x-axis dates)
      if (!this.labels || this.labels.length === 0) return [];
      
      // Convert all labels to dates and find min/max
      const dates = this.labels.map(label => new Date(label));
      const startDate = new Date(Math.min(...dates));
      const endDate = new Date(Math.max(...dates));
      
      // Only include issues that have been resolved within the displayed date range
      return filteredIssues
        .filter(issue => {
          if (!issue.date_resolved || issue.date_resolved === null) return false;
          
          const resolvedDate = new Date(issue.date_resolved);
          return resolvedDate >= startDate && resolvedDate <= endDate;
        })
        .map(issue => {
          const annotationId = `issue-${issue.id}`;
          // Store mapping for click handler
          this.issueAnnotationMap[annotationId] = issue.id;
          
          return {
            x: new Date(issue.date_resolved).getTime(),
            borderColor: '#FF6384',
            label: {
              borderColor: '#FF6384',
              style: {
                color: '#fff',
                background: '#FF6384',
                cursor: 'pointer',
              },
              text: issue.title.length > 30 ? issue.title.substring(0, 30) + '...' : issue.title,
              position: 'top',
              cssClass: annotationId,
            },
            id: annotationId,
          };
        });
    },
    getYaxisAnnotations(metric) {
      const annotations = {
        yaxis: [],
        xaxis: [], // Initialize empty, will be filled after labels are set
      };
      annotations.yaxis.push({
        y: fullRanges[metric][0][0],
        y2: fullRanges[metric][0][1],
        fillColor: rangesColors["green"],
      });
      annotations.yaxis.push({
        y: fullRanges[metric][1][0],
        y2: fullRanges[metric][1][1],
        fillColor: rangesColors["orange"],
      });
      annotations.yaxis.push({
        y: fullRanges[metric][2][0],
        y2: fullRanges[metric][2][1],
        fillColor: rangesColors["red"],
      });
      this.annotations = null;
      return annotations;
    },
    async getData() {
      // if (!this.applyFilters) {
      //   return;
      // }
      const params = {};
      if (this.sites && this.sites.length > 0) {
        params.sites = this.sites.join(",");
      }
      if (this.pageTypes && this.pageTypes.length > 0) {
        params["content_types"] = this.pageTypes.join(",");
      }
      if (this.devices && this.devices.length > 0) {
        params.devices = this.devices.join(",");
      }
      if (this.dates) {
        params.dates = this.dates.join(",");
      }
      this.loading = true;
      const { data } = await this.axiosGet(
        "/web-perf/cwv-detailed-performance",
        params
      );
      if (data) {
        this.items = data["daily_data"];
        this.cruxItems = data["daily_data_crux"];
        this.globalData = data["global_data"];
        this.contentTypes = this.formatContentTypesData(
          data["content_types_data"]
        );

        this.contentTypes = this.contentTypes.map((item) => ({
          ...item,
          content_type:
            item.content_type == "null"
              ? "empty_content_type"
              : item.content_type,
        }));
        this.fillData();
      }
      this.loading = false;
      // Force clicking on button again to re-run that request :
      // (we don't call this dispatch earlier to be sure, as state is async, that all listening components could run their requests)
      this.$store.dispatch("common/updateApplyFilters", false);
    },
    formatContentTypesData(data) {
      const contentTypes = [];
      for (const [key, value] of Object.entries(data)) {
        const entry = {
          content_type: key,
          lcp: value.lcp,
          cls: value.cls,
          inp: value.inp,
          fcp: value.fcp,
          ttfb: value.ttfb,
          count: value.count,
        };
        contentTypes.push(entry);
      }
      return contentTypes;
    },
    fillData() {
      const data = this.items[this.selectedMetric];
      const cruxData = this.cruxItems[this.selectedMetric];
      this.labels = [];
      this.series = [];
      for (let device in data) {
        const d = data[device];
        const deviceName = this.getDeviceName(parseInt(device));
        const element = {
          name: deviceName,
          data: [],
          baseDeviceName: deviceName,
        };
        for (let v in d) {
          element.data.push(d[v]);
        }
        this.series.push(element);
      }
      for (let device in cruxData) {
        const d = cruxData[device];
        const deviceName = this.getDeviceName(parseInt(device));
        const element = {
          name: `${deviceName} (CrUX)`,
          data: [],
          baseDeviceName: deviceName,
        };
        for (let v in d) {
          element.data.push(d[v]);
        }
        this.series.push(element);
      }
      if (!this.series.length) {
        this.series.push({
          name: "Aucun résultat",
          data: [],
        });
        return;
      }
      this.labels = Object.keys(data[Object.keys(data)[0]]) || [];
      
      // Get annotations with y-axis ranges
      this.annotations = this.getYaxisAnnotations(this.selectedMetric);
      
      // Add x-axis annotations if showIssues is true and we have labels
      if (this.showIssues && this.labels.length > 0) {
        this.annotations.xaxis = this.getXaxisAnnotationsFromIssues(this.selectedMetric);
      }
      
      // Re-attach click listeners after chart updates
      this.$nextTick(() => {
        setTimeout(() => {
          this.attachAnnotationClickListeners();
        }, 500);
      });
    },
    selectTab(index) {
      this.selectedTab = index;
      this.fillData();
    },
    async openIssueEditModal(issueId) {
      await this.$store.dispatch("webPerf/updateSelectedIssueId", issueId);
      await this.$store.dispatch("webPerf/updateIssueModalVisibility", true);
    },
    attachAnnotationClickListeners() {
      // Find all annotation labels and attach click listeners
      Object.entries(this.issueAnnotationMap).forEach(([annotationId, issueId]) => {
        const elements = document.querySelectorAll(`.${annotationId}`);
        elements.forEach(element => {
          element.style.cursor = 'pointer';
          element.addEventListener('click', () => {
            this.openIssueEditModal(issueId);
          });
        });
      });
    },
  },
  computed: {
    applyFilters() {
      return this.$store.getters["common/getApplyFilters"];
    },
    sites() {
      console.log("sites", this.$store.getters["webPerf/getSites"]);
      return this.$store.getters["webPerf/getSites"];
    },
    pageTypes() {
      return this.$store.getters["webPerf/getPageTypes"];
    },
    devices() {
      return this.$store.getters["webPerf/getDevices"];
    },
    dates() {
      return this.$store.getters["webPerf/getDates"];
    },
    selectedMetric() {
      return this.tabs[this.selectedTab];
    },
  },
  watch: {
    applyFilters() {
      this.getData();
      this.getIssues();
    },
    showIssues() {
      this.fillData();
      // Re-attach listeners after chart updates
      this.$nextTick(() => {
        setTimeout(() => {
          this.attachAnnotationClickListeners();
        }, 500);
      });
    },
  },
};
</script>

<style scoped>
.tabs-container {
  .tab-selector {
    border: 1px solid #e0e6ee;
    background-color: #f4f5fa;
    padding: 14px;
    box-shadow: 0 0 0px #e0e0e0;
    border-radius: 5px;
    cursor: pointer;
    transition: box-shadow 0.3s ease, transform 0.3s ease;
    font-weight: bold;
    color: #565656;

    &:not(:first-child) {
      margin: 10px 0;
    }

    &:hover {
      background-color: #f4f6ff;
      box-shadow: 0 5px 15px rgba(0, 0, 0, 0.15);
      transform: translateY(-3px);
    }

    &:active {
      background-color: #f4f5fa;
      box-shadow: 0 0 15px rgba(0, 0, 0, 0.15);
      transform: translateY(0);
    }

    &.selected {
      background-color: #fff;
      box-shadow: 0 5px 15px rgba(0, 0, 0, 0.15);
      //transform: translateX(6px);
    }
  }
}

.graph-container {
  background-color: #f5f5f5;
  border-radius: 5px;
}
</style>
